<template>
  <v-dialog :value="value" @input="$emit('input', $event)" width="unset">
    <v-card>
      <v-card-title>{{ $t('global.billing.billingInformation') }}</v-card-title>
      <!-- <v-card-subtitle>
        {{ $t('global.welcomeMessage') }}
      </v-card-subtitle> -->

      <v-form ref="form" v-model="valid" style="padding: 24px">
        <v-overlay :value="loading" absolute>
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <v-text-field
          v-model="form.firstName"
          :label="$t('global.billing.firstName')"
          :rules="[rules.required]"
        ></v-text-field>
        <v-text-field
          v-model="form.lastName"
          :label="$t('global.billing.lastName')"
          :rules="[rules.required]"
        ></v-text-field>
        <v-text-field v-model="form.company" :label="$t('global.billing.company')"></v-text-field>
        <v-text-field
          v-model="form.companyId"
          :label="$t('global.billing.companyId')"
        ></v-text-field>
        <v-text-field v-model="form.vat" :label="$t('global.billing.vat')"></v-text-field>
        <v-text-field
          v-model="form.email"
          :label="$t('global.billing.email')"
          :rules="[rules.required, rules.email]"
          type="email"
        ></v-text-field>

        <v-row>
          <v-col cols="5">
            <v-select
              v-model="form.phonePrefix"
              :items="phonePrefixes"
              :label="$t('global.billing.phonePrefix')"
            ></v-select>
          </v-col>
          <v-col cols="7">
            <v-text-field
              v-model="form.phoneNumber"
              :label="$t('global.billing.phone')"
              :rules="[rules.phone]"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-select
          v-model="form.country"
          :items="countryItems"
          item-text="text"
          item-value="value"
          :label="$t('global.billing.country')"
          :rules="[rules.required]"
        ></v-select>

        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="form.street"
              :label="$t('global.billing.street')"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="form.streetNumber"
              :label="$t('global.billing.streetNumber')"
              :rules="[rules.numeric]"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="form.apartmentNumber"
              :label="$t('global.billing.apartmentNumber')"
              :rules="[rules.numeric]"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="form.zipCode"
              :label="$t('global.billing.zipCode')"
              :rules="[rules.required, rules.numeric]"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.city"
              :label="$t('global.billing.city')"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Currency Selection -->
        <v-row>
          <v-col cols="12">
            <v-select
              v-model="selectedCurrency"
              :items="availableCurrencies"
              :label="$t('global.selectedCurrency')"
              @change="updateDisplayedPrice"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Display Price in Selected Currency -->
        <v-row>
          <v-col cols="12">
            <h3>{{ $t('global.price') }}: {{ displayedPrice }} {{ selectedCurrency }}</h3>
          </v-col>
        </v-row>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="onClose()" depressed>
            {{ $t('global.cancel') }}
          </v-btn>
          <v-btn color="orange" dark @click="submitForm" depressed>
            {{ $t('global.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { subscriptionRenew } from '@/graphql/mutations/subscriptionRenew'
import { deviceBillingInfo } from '@/graphql/query/deviceBillingInfo'
import { countryCodesWithName } from '@/config/countryCodes'

export default {
  name: 'SubscriptionRenewDialog',

  data() {
    return {
      valid: false,
      loading: false,
      form: {
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        phonePrefix: '+420',
        phoneNumber: '',
        country: 'CZ',
        street: '',
        streetNumber: '',
        apartmentNumber: '',
        zipCode: '',
        city: '',
        companyId: '',
        vat: '',
      },
      billingInfo: {},
      phonePrefixes: ['+420', '+421', '+48', '+1', '+44', '+49', '+33', '+39', '+34', '+43'],
      countryItems: Object.entries(countryCodesWithName).map(([code, name]) => ({
        text: name,
        value: code,
      })),
      availableCurrencies: [], // New data property for available currencies
      selectedCurrency: 'CZK', // Default selected currency
      displayedPrice: 0, // Price to display based on selected currency
      rules: {
        required: (value) => !!value || this.$t('global.requiredField'),
        phone: (value) => !value || /^\d{9,15}$/.test(value) || this.$t('global.invalidPhone'),
        zipCode: (value) => /^\d{3} ?\d{2}$/.test(value) || this.$t('global.invalidZipCode'),
        numeric: (value) => !value || /^\d+$/.test(value) || this.$t('global.numericField'),
        email: (value) => /.+@.+\..+/.test(value) || this.$t('global.invalidEmail'),
      },
    }
  },

  apollo: {
    billingInfo: {
      query: deviceBillingInfo,
      variables() {
        return {
          deviceId: this.device.id,
        }
      },
      update(data) {
        if (data?.billingInfo) {
          const billing = data.billingInfo
          // Extract phone prefix and number
          const phoneMatch = billing.phone?.match(/^(\+\d+)\s*(\d+)$/)
          const phonePrefix = phoneMatch ? phoneMatch[1] : '+420'
          const phoneNumber = phoneMatch ? phoneMatch[2] : ''

          this.form = {
            firstName: billing.firstName || '',
            lastName: billing.lastName || '',
            company: billing.company || '',
            email: billing.email || '',
            phonePrefix: phonePrefix,
            phoneNumber: phoneNumber,
            country: billing.country || 'CZ',
            street: billing.street || '',
            streetNumber: billing.streetNumber || '',
            apartmentNumber: billing.apartmentNumber || '',
            zipCode: billing.zipCode || '',
            city: billing.city || '',
            companyId: billing.companyId || '',
            vat: billing.vat || '',
          }

          // Fetch available currencies from billing info
          this.availableCurrencies = billing.prices.map((price) => price.currency)
          this.billingInfo = billing

          this.selectedCurrency = billing.prices[0].currency

          this.displayedPrice = (billing.prices[0].price / 100).toFixed(2)
          this.updateDisplayedPrice()
        }
        return data?.billingInfo
      },
      loadingKey: 'loading',
    },
  },

  methods: {
    async submitForm() {
      if (this.$refs.form.validate()) {
        try {
          const paymentData = {
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            company: this.form.company,
            phone: this.form.phonePrefix + this.form.phoneNumber,
            country: this.form.country,
            street: this.form.street,
            streetNumber: this.form.streetNumber,
            apartmentNumber: this.form.apartmentNumber,
            zipCode: this.form.zipCode,
            city: this.form.city,
            email: this.form.email || '',
            companyId: this.form.companyId || '',
            vat: this.form.vat || '',
            currency: this.selectedCurrency,
          }

          const query = await this.$apollo.mutate({
            mutation: subscriptionRenew,
            variables: {
              paymentData,
              deviceId: this.device.id,
            },
          })

          const data = query.data

          console.log('DATA', data)

          if (data?.initTransaction) {
            const response = data.initTransaction
            console.log('RESPONSE', response)
            if (response?.code === 0 || response?.code === '0') {
              this.$toast.success(this.$t('global.subscriptionRenewalSuccess'))
              if (response.redirect) {
                window.location.replace(response?.redirect)
              } else {
                this.onClose()
              }
            } else {
              this.$toast.error(response?.message || this.$t('global.subscriptionRenewalError'))
            }
          } else {
            this.$toast.error(this.$t('global.subscriptionRenewalError'))
          }
        } catch (error) {
          console.error('Error renewing subscription:', error)
          this.$toast.error(error.message || this.$t('global.subscriptionRenewalError'))
        }
      } else {
        this.$toast.error(this.$t('global.fillRequiredFields'))
      }
    },
    updateDisplayedPrice() {
      // Calculate the total price in the selected currency
      const totalPrice = this.billingInfo.prices.reduce((total, price) => {
        if (price.currency === this.selectedCurrency) {
          return total + price.price // Add price if it matches the selected currency
        }
        return total
      }, 0)

      // Convert total price from cents to the appropriate format (e.g., dollars)
      this.displayedPrice = (totalPrice / 100).toFixed(2) // Update displayed price in dollars
    },
  },
  props: {
    value: Boolean,
    device: {
      type: Object,
      required: true,
    },
    onClose: Function,
  },
}
</script>

<style scoped>
.v-container {
  max-width: 600px;
}

form {
  padding: 16px;
}
</style>
